import type { TranslateService } from '@ngx-translate/core';
import { BackendMenuItem } from '../shared/models/menu-item.model';

/**
 * Variante visual del badge inline del menu (la "etiqueta" tipo "New",
 * "BETA" que el legacy meteia con `<span class="notification blue">...`).
 * Se mapea a colores del theme — sin clases huerfanas.
 */
export type NavBadgeVariant = 'info' | 'success' | 'warning' | 'danger' | 'accent' | 'neutral';

export interface NavBadge {
  text: string;
  variant: NavBadgeVariant;
}

export interface AppNavItem {
  // Identificador unico del item dentro del arbol (lo usa dx-tree-view como keyExpr).
  // Lo derivamos del Modulo.Id del backend para garantizar unicidad.
  key: string;
  text: string;
  icon?: string;
  path?: string;
  items?: AppNavItem[];
  /** Badge inline a la derecha del texto. El backend lo embebe via HTML
   *  legacy (ej. `<span class="notification blue">New</span>`) y nosotros
   *  lo extraemos a una estructura mappable. */
  badge?: NavBadge;
}

/**
 * Mapeo URL legacy → ruta Angular existente en frontend-crm.
 * Las URLs vienen como "/Cliente/Index" o "Cliente/Index".
 * Si una URL no tiene ruta mapeada, el item se oculta (la pantalla aún no fue migrada).
 */
const URL_TO_ROUTE: Record<string, string> = {
  // Seguridad / Setup
  usuario: '/usuarios',
  usuarios: '/usuarios',
  rol: '/roles',
  roles: '/roles',
  // Comercial
  cliente: '/clientes',
  clientes: '/clientes',
  contacto: '/contactos',
  contactos: '/contactos',
  // Catálogos / Personas
  industria: '/industrias',
  industrias: '/industrias',
  // Vista unificada (Personal): los 3 modulos legacy redirijen a /personal con tab.
  empleadosenionics: '/personal?tab=ionics',
  personasenplanta: '/personal?tab=planta',
  personasenespera: '/personal?tab=espera',
  personal: '/personal',
  empleadosenplanta: '/empleados-en-planta',
  // Productos
  producto: '/productos',
  productos: '/productos',
  // Operaciones — Pedidos
  pedidoproceso: '/pedidos',
  pedido: '/pedidos',
  pedidos: '/pedidos',
  cronogramapedido: '/cronograma',
  cronogramapedidonew: '/cronograma',
  cronograma: '/cronograma',
  controlactividadarea: '/control-actividad',
  controlactividad: '/control-actividad',
  secuenciamiento: '/secuenciamiento',
  // Calidad
  eventualidad: '/eventualidades',
  eventualidades: '/eventualidades',
  tipoeventualidad: '/tipos-eventualidad',
  tiposeventualidad: '/tipos-eventualidad',
  tiposeventualidades: '/tipos-eventualidad',
  subtipoeventualidad: '/sub-tipos-eventualidad',
  subtiposventualidad: '/sub-tipos-eventualidad',
  subtipoevntualidad: '/sub-tipos-eventualidad',
  noconformidaddos: '/no-conformidades',
  noconformidad: '/no-conformidades',
  noconformidades: '/no-conformidades',
  // Comunicación
  comunicacioninterna: '/mails',
  mail: '/mails',
  mails: '/mails',
  // Actividad
  novedad: '/novedades',
  novedades: '/novedades',
  tiponovedad: '/tipos-novedad',
  tiposnovedad: '/tipos-novedad',
  tiposnovedades: '/tipos-novedad',
  // Asignacion de Perfil — legacy `RolEmpresa/Index`. Aliases incluyen las
  // 2 variantes mas usadas en RolModulos.URL del legacy.
  rolempresa: '/asignacion-perfil',
  asignacionperfil: '/asignacion-perfil',
  asignacionperfiles: '/asignacion-perfil',
  // Parametros del sistema — legacy `Parametros/Index`. Aliases incluyen
  // `parametrosgenerales` (la entrada del menu legacy en Configuracion).
  parametro: '/parametros',
  parametros: '/parametros',
  parametrosgenerales: '/parametros',
  // Tipos de Acontecimiento (legacy `TiposAcontecimiento/Index`)
  tipoacontecimiento: '/tipos-acontecimiento',
  tiposacontecimiento: '/tipos-acontecimiento',
  tiposacontecimientos: '/tipos-acontecimiento',
  // Origenes de No Conformidad (legacy `Origen/Index` — controller stub)
  origen: '/origenes-no-conformidad',
  origenes: '/origenes-no-conformidad',
  origennoconformidad: '/origenes-no-conformidad',
  origenesnoconformidad: '/origenes-no-conformidad',
  origenesnoconformidades: '/origenes-no-conformidad',
  // Tipos de No Conformidad (catalogo — sin controller en legacy)
  tiponoconformidad: '/tipos-no-conformidad',
  tiposnoconformidad: '/tipos-no-conformidad',
  tiposnoconformidades: '/tipos-no-conformidad',
  // Acciones de No Conformidad (catalogo — sin controller en legacy)
  accion: '/acciones-no-conformidad',
  acciones: '/acciones-no-conformidad',
  accionnoconformidad: '/acciones-no-conformidad',
  accionesnoconformidad: '/acciones-no-conformidad',
  accionesnoconformidades: '/acciones-no-conformidad',
  acontecimiento: '/acontecimientos',
  acontecimientos: '/acontecimientos',
  // Analítica
  cubo: '/home',
  paneldecontrol: '/home',
  apicubo: '/home',
  cubotest: '/cubo-test',
  // Documentos vigentes (legacy `Biblioteca/Index`)
  biblioteca: '/biblioteca',
  documentosvigentes: '/biblioteca',
};

/**
 * Mapeo class legacy (CSS class del icono) → icono DevExtreme.
 * El legacy usaba clases tipo "fa-user", "fa-cog", etc. Si no hay match, usamos un default.
 */
const CLASS_TO_ICON: Record<string, string> = {
  'fa fa-user': 'user',
  'fa-user': 'user',
  'fa fa-users': 'group',
  'fa-users': 'group',
  'fa fa-cog': 'preferences',
  'fa-cog': 'preferences',
  'fa fa-cogs': 'preferences',
  'fa-cogs': 'preferences',
  'fa fa-file': 'doc',
  'fa-file': 'doc',
  'fa fa-folder': 'folder',
  'fa-folder': 'folder',
  'fa fa-chart-bar': 'chart',
  'fa-chart-bar': 'chart',
  'fa fa-bar-chart': 'chart',
  'fa-bar-chart': 'chart',
  'fa fa-shopping-cart': 'cart',
  'fa-shopping-cart': 'cart',
  'fa fa-envelope': 'email',
  'fa-envelope': 'email',
  'fa fa-bell': 'belloutline',
  'fa-bell': 'belloutline',
  'fa fa-calendar': 'event',
  'fa-calendar': 'event',
  'fa fa-tasks': 'check',
  'fa-tasks': 'check',
  'fa fa-home': 'home',
  'fa-home': 'home',
};

const DEFAULT_ICON_BY_TEXT: Array<[RegExp, string]> = [
  [/cliente|contacto/i, 'group'],
  [/pedido/i, 'cart'],
  [/eventual|conformid/i, 'warning'],
  [/mail|comunic/i, 'email'],
  [/novedad/i, 'belloutline'],
  [/cubo|panel|analiti|reporte/i, 'chart'],
  [/calend|agenda/i, 'event'],
  [/usuari|rol|seguridad|param/i, 'preferences'],
  [/archiv/i, 'doc'],
];

/**
 * Mapeo de las clases de color que usaba el legacy en el HTML inline del
 * menu (`<span class="notification blue">New</span>`). El legacy queria
 * que esas clases existieran en CSS — en el nuevo CRM no las definimos
 * sueltas y mappeamos a las variantes del theme.
 */
const LEGACY_BADGE_CLASS_TO_VARIANT: Record<string, NavBadgeVariant> = {
  blue: 'info',
  primary: 'info',
  green: 'success',
  success: 'success',
  yellow: 'warning',
  warning: 'warning',
  orange: 'warning',
  red: 'danger',
  danger: 'danger',
  pink: 'danger',
  purple: 'accent',
  accent: 'accent',
  gray: 'neutral',
  grey: 'neutral',
  default: 'neutral',
};

/**
 * Detecta clases de variante en el HTML legacy. Acepta variantes cualquier
 * orden (`notification blue` o `blue notification`), match case-insensitive.
 */
function classListToBadgeVariant(classAttr: string): NavBadgeVariant {
  const tokens = classAttr.toLowerCase().split(/\s+/).filter(Boolean);
  for (const t of tokens) {
    if (t in LEGACY_BADGE_CLASS_TO_VARIANT) return LEGACY_BADGE_CLASS_TO_VARIANT[t];
  }
  // Variante por defecto si solo se ve `notification` o `badge` sin color.
  return 'info';
}

/**
 * Parsea HTML inline del legacy (badges, span, etc.) que el backend mete
 * dentro de `Modulo.Descripcion`. Extrae el texto plano + el primer badge
 * que encuentre. Si no hay HTML, devuelve el texto como esta.
 *
 * Ejemplos soportados:
 *   "Cronograma de Pedidos <span class='notification blue'>New</span>"
 *   "Reportes <span class=\"notification red\">3</span>"
 *   "Pedidos <span class='badge yellow'>BETA</span>"
 *
 * Seguridad: NO devolvemos HTML al template — solo strings/objetos. Asi
 * evitamos `[innerHTML]` y la superficie de XSS (un admin malicioso que
 * configure el menu podria meter `<script>` en Modulo.Descripcion).
 */
function parseInlineBadge(raw: string): { text: string; badge?: NavBadge } {
  if (!raw || raw.indexOf('<') === -1) {
    return { text: raw?.trim() ?? '' };
  }

  // Match cualquier <span class="..."> ... </span>. No anidado, no <script>.
  // Captura: 1=class attr, 2=texto interior.
  const re = /<span[^>]*\bclass\s*=\s*["']([^"']+)["'][^>]*>([^<]*)<\/span>/i;
  const m = raw.match(re);

  if (!m) {
    // Hay tags pero no es un span con class — strip todo el HTML para safety.
    return { text: raw.replace(/<[^>]*>/g, '').trim() };
  }

  const badgeText = m[2].trim();
  const variant = classListToBadgeVariant(m[1]);
  // Texto del item = todo menos el span del badge, con HTML strippeado.
  const itemText = raw.replace(re, '').replace(/<[^>]*>/g, '').trim();

  if (!badgeText) return { text: itemText };
  return {
    text: itemText,
    badge: { text: badgeText, variant },
  };
}

function legacyControllerFromUrl(url: string | null | undefined): string {
  if (!url) return '';
  const trimmed = url.trim().replace(/^[~/]+/, '');
  const slash = trimmed.indexOf('/');
  return (slash <= 0 ? trimmed : trimmed.substring(0, slash)).toLowerCase();
}

/**
 * Slugifica un texto para usar como i18n key — quita tildes, lowercase,
 * solo a-z0-9 (sin espacios ni simbolos). Lo usamos para mapear los textos
 * de los grupos del menu (sin URL/controller) a `menu.grupos.<slug>`.
 *
 * Ej: "Configuración" → "configuracion", "No-Conformidades" → "noconformidades".
 */
function slugifyMenu(s: string): string {
  // NFD descompone "ó" → "o" + acento combinante (U+0301). El segundo replace
  // saca el rango U+0300..U+036F (combining diacritical marks).
  return (s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Traduce el texto de un item del menu intentando, en orden:
 *  1. `menu.modulos.<ctrlKey>` — si es hoja con controller del legacy mapeado.
 *  2. `menu.grupos.<slug(text)>` — si es grupo o no hay controller.
 *  3. Fallback: el texto original del backend (graceful degradation).
 *
 * `translate.instant(key)` devuelve la propia key cuando no esta definida —
 * ese es el sentinel que usamos para detectar "no hubo traduccion" y caer al
 * siguiente nivel.
 */
function translateMenuText(
  translate: TranslateService | undefined,
  rawText: string,
  ctrlKey: string,
): string {
  if (!translate) return rawText;
  if (ctrlKey) {
    const key = `menu.modulos.${ctrlKey}`;
    const t = translate.instant(key);
    if (t && t !== key) return t;
  }
  const slug = slugifyMenu(rawText);
  if (slug) {
    const groupKey = `menu.grupos.${slug}`;
    const t = translate.instant(groupKey);
    if (t && t !== groupKey) return t;
  }
  return rawText;
}

function mapIcon(item: BackendMenuItem): string {
  if (item.class) {
    const key = item.class.trim().toLowerCase();
    if (CLASS_TO_ICON[key]) return CLASS_TO_ICON[key];
    for (const [k, icon] of Object.entries(CLASS_TO_ICON)) {
      if (key.includes(k)) return icon;
    }
  }
  for (const [re, icon] of DEFAULT_ICON_BY_TEXT) {
    if (re.test(item.descripcion)) return icon;
  }
  // Fallback genérico — garantiza que TODOS los items tengan icono y queden alineados.
  return 'detailslayout';
}

/**
 * Mappea la estructura del backend al modelo del nav. Si se pasa `translate`,
 * los textos se resuelven contra los namespaces `menu.modulos.*` (hojas) y
 * `menu.grupos.*` (grupos). Sin `translate` → usa los textos crudos del
 * backend (modo legacy / fallback).
 *
 * El caller (NavigationService) cachea el backend original y re-llama esta
 * funcion en `translate.onLangChange` para refrescar el menu sin re-fetch.
 */
export function mapBackendMenuToNav(
  items: BackendMenuItem[],
  translate?: TranslateService,
): AppNavItem[] {
  const result: AppNavItem[] = [];
  for (const item of items) {
    const children = item.items?.length ? mapBackendMenuToNav(item.items, translate) : undefined;
    const isLeaf = !!item.url;
    const key = `m${item.id}`;

    // Parsea HTML inline del legacy (badges) de la descripcion.
    const parsed = parseInlineBadge(item.descripcion);

    if (isLeaf) {
      // Hoja: si tiene ruta mapeada navega ahi; si no, mandamos al placeholder
      // generico /legacy/:controller?name=Descripcion para que el usuario al menos
      // vea que el modulo existe y esta pendiente de migracion.
      const ctrl = legacyControllerFromUrl(item.url);
      const mappedRoute = URL_TO_ROUTE[ctrl];
      const text = translateMenuText(translate, parsed.text, ctrl);
      const path = mappedRoute
        ? mappedRoute
        : `/legacy/${ctrl}?name=${encodeURIComponent(text)}`;
      result.push({
        key,
        text,
        icon: mapIcon(item),
        path,
        badge: parsed.badge,
      });
    } else {
      // Grupo: incluir si tiene hijos.
      if (!children || children.length === 0) continue;
      result.push({
        key,
        text: translateMenuText(translate, parsed.text, ''),
        icon: mapIcon(item),
        path: '',
        items: children,
        badge: parsed.badge,
      });
    }
  }
  return result;
}
